<script setup>
/**
 * Baandet paa produktsiden: den her vare er en del, og vi kan bygge med den.
 *
 * MAALT 14. oktober 2026: 3.244 af vores produktsider er dele i byggeren og
 * fik 10.856 eksponeringer paa seks uger. Byggeren fik 11. Ingen af de 3.244
 * sider naevnte at vaerktoejet findes.
 *
 * Serveren sender null paa alt der ikke er en del, saa sektionen findes ikke
 * paa en stoevsuger — se Komponentbaand.php.
 *
 * KNAPPEN AABNER BYGGEREN MED DELEN VALGT og lægger den til det man i forvejen
 * havde i kladden. /pc-bygger/vaelg/ er spaerret i robots.txt, saa det
 * crawlbare link er /pc-bygger og build-siderne; knappens adresse er
 * noindex,follow fordi der er 22.089 dele og lige saa mange udgaver af den
 * samme bygger.
 */
import { computed } from 'vue';
import { Link } from '@inertiajs/vue3';
import Buildkort from '@/Components/PcBygger/Buildkort.vue';

const props = defineProps({
    baand: { type: Object, required: true },
});

const antal = computed(() =>
    new Intl.NumberFormat('da-DK').format(props.baand.antal_i_byggeren || 0)
);

const buildsOverskrift = computed(() =>
    props.baand.builds_bruger_delen
        ? 'Færdige maskiner der bruger denne del'
        : `Færdige maskiner med ${props.baand.navne.ubestemt}`
);

/*
 * En skaerm er ikke noget man bruger "i en hel PC" paa samme maade som et
 * grafikkort, og den har hverken sokkel eller stroemkrav vi kan regne paa.
 * Begge saetninger skifter derfor med gruppen. Se Byggegrupper.
 */
const overskrift = computed(() =>
    props.baand.er_maskinen
        ? `Brug ${props.baand.navne.bestemt} i en hel PC`
        : `Tag ${props.baand.navne.bestemt} med i et PC-build`
);
</script>

<template>
    <section class="card kb">
        <div class="card-body">
            <h2 class="h5 mb-2">{{ overskrift }}</h2>

            <p class="kb-intro mb-3">
                <Link href="/pc-bygger">PC-byggeren</Link>
                <template v-if="baand.er_maskinen">
                    sætter en maskine sammen omkring denne vare og holder øje med at
                    delene passer: sokkel, hukommelsestype, plads i kabinettet og strøm.
                </template>
                <template v-else>
                    lægger denne vare oven i maskinen, så prisen på hele opsætningen
                    står ét sted.
                </template>
                Den har {{ antal }} {{ baand.navne.flertal }} at sammenligne med.
            </p>

            <!--
                Kompatibilitetstallene. Kun de felter vi HAR — en linje der
                siger "Sokkel: ukendt" er vaerre end ingen linje, og det er
                ogsaa de tal byggeren selv regner paa.
            -->
            <dl v-if="baand.fakta.length" class="kb-fakta mb-3">
                <div v-for="f in baand.fakta" :key="f.etiket">
                    <dt>{{ f.etiket }}</dt>
                    <dd>{{ f.vaerdi }}</dd>
                </div>
            </dl>

            <p class="mb-0">
                <Link :href="baand.bygger_url" class="btn btn-sm btn-pricecompare-primary">
                    <i class="bi bi-pc-display me-1"></i>
                    Åbn PC-byggeren med {{ baand.navne.bestemt }}
                </Link>
            </p>

            <template v-if="baand.builds.length">
                <h3 class="h6 mt-4 mb-3">{{ buildsOverskrift }}</h3>

                <div class="kb-gitter">
                    <Buildkort v-for="b in baand.builds" :key="b.slug" :kort="b" />
                </div>
            </template>
        </div>
    </section>
</template>

<style scoped>
.kb-intro {
    color: var(--pc-text-secondary);
    max-width: 62ch;
}

.kb-fakta {
    margin: 0;
    display: grid;
    gap: .3rem .9rem;
    grid-template-columns: repeat(auto-fit, minmax(14rem, 1fr));
}
.kb-fakta > div {
    display: grid;
    grid-template-columns: 11rem 1fr;
    gap: .5rem;
    align-items: baseline;
    border-bottom: 1px solid var(--pc-border);
    padding-bottom: .25rem;
}
.kb-fakta dt {
    font-size: .8rem;
    color: var(--pc-text-muted);
    font-weight: 500;
}
.kb-fakta dd {
    margin: 0;
    font-size: .88rem;
    font-weight: 600;
}

.kb-gitter {
    display: grid;
    gap: 1rem;
    grid-template-columns: repeat(auto-fit, minmax(16rem, 1fr));
}

@media (max-width: 575px) {
    .kb-fakta > div { grid-template-columns: 1fr; gap: 0; }
}
</style>
